import React, { useState, useEffect, useCallback, useMemo } from 'react';
import nbrApi from '../../../services/nbrApi';
import moment from 'moment';
import { Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import '../../../css/multi-property-monitor.css';

const SETTINGS_STORAGE_KEY = 'timecardReportSettings';

const RANGE_OPTIONS = [
    { value: 'today', label: 'Today' },
    { value: 'this_week', label: 'This Week' },
    { value: 'this_month', label: 'This Month' }
];

const GROUPING_OPTIONS = [
    { value: 'resource', label: 'By Resource' },
    { value: 'date', label: 'By Date' },
    { value: 'role', label: 'By Role' }
];

function TimecardReport() {
    const selectedLoginData = JSON.parse(localStorage.getItem('selectedLoginData')) || {};
    const propertiesId = selectedLoginData?.properties_id;

    // State management
    const [fromDate, setFromDate] = useState(moment().format('YYYY-MM-DD'));
    const [toDate, setToDate] = useState(moment().format('YYYY-MM-DD'));
    const [activeRange, setActiveRange] = useState('today');
    const [timecardData, setTimecardData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState({});
    const [controlsExpanded, setControlsExpanded] = useState(false);
    const [savedVisibleColumns, setSavedVisibleColumns] = useState(null);
    const [settingsLoaded, setSettingsLoaded] = useState(false);
    const [groupingOption, setGroupingOption] = useState('none');
    const [expandedGroups, setExpandedGroups] = useState({});

    // Read settings from localStorage
    const readSettings = () => {
        try {
            const stored = localStorage.getItem(SETTINGS_STORAGE_KEY);
            if (stored) {
                return JSON.parse(stored);
            }
        } catch (error) {
            console.error('Failed to parse timecard report settings:', error);
        }
        return null;
    };

    // Write settings to localStorage
    const writeSettings = (settings) => {
        try {
            localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
        } catch (error) {
            console.error('Failed to write timecard report settings:', error);
        }
    };

    // Apply date range presets
    const applyDateRange = (rangeKey) => {
        switch (rangeKey) {
            case 'today': {
                const todayStart = moment().startOf('day');
                setFromDate(todayStart.format('YYYY-MM-DD'));
                setToDate(todayStart.format('YYYY-MM-DD'));
                setActiveRange(rangeKey);
                break;
            }
            case 'this_week': {
                const startOfWeek = moment().startOf('isoWeek');
                setFromDate(startOfWeek.format('YYYY-MM-DD'));
                setToDate(startOfWeek.clone().endOf('isoWeek').format('YYYY-MM-DD'));
                setActiveRange(rangeKey);
                break;
            }
            case 'this_month': {
                const startOfMonth = moment().startOf('month');
                setFromDate(startOfMonth.format('YYYY-MM-DD'));
                setToDate(startOfMonth.clone().endOf('month').format('YYYY-MM-DD'));
                setActiveRange(rangeKey);
                break;
            }
            default:
                break;
        }
    };

    // Get all columns from data
    const allColumns = useMemo(() => {
        if (timecardData.length === 0) return [];
        return Object.keys(timecardData[0]);
    }, [timecardData]);

    // Get visible columns in order
    const visibleColumnOrder = useMemo(
        () => allColumns.filter(column => visibleColumns[column]),
        [allColumns, visibleColumns]
    );

    // Initialize visible columns from saved settings or default to all visible
    useEffect(() => {
        if (allColumns.length === 0) {
            setVisibleColumns({});
            return;
        }

        setVisibleColumns(prev => {
            const next = {};

            allColumns.forEach(column => {
                if (prev && Object.prototype.hasOwnProperty.call(prev, column)) {
                    next[column] = prev[column];
                } else if (savedVisibleColumns && Object.prototype.hasOwnProperty.call(savedVisibleColumns, column)) {
                    next[column] = savedVisibleColumns[column];
                } else {
                    // Default: all columns visible
                    next[column] = true;
                }
            });

            return next;
        });
    }, [allColumns, savedVisibleColumns]);

    // Save settings when they change
    useEffect(() => {
        if (!settingsLoaded) {
            return;
        }

        writeSettings({
            visibleColumns,
            activeRange,
            fromDate,
            toDate,
            controlsExpanded,
            groupingOption
        });
    }, [visibleColumns, activeRange, fromDate, toDate, controlsExpanded, groupingOption, settingsLoaded]);

    // Load settings on mount
    useEffect(() => {
        const settings = readSettings();

        if (settings) {
            if (settings.visibleColumns) {
                setSavedVisibleColumns(settings.visibleColumns);
            }

            if (settings.activeRange && settings.activeRange !== 'custom') {
                applyDateRange(settings.activeRange);
            } else {
                if (settings.fromDate) {
                    setFromDate(settings.fromDate);
                }
                if (settings.toDate) {
                    setToDate(settings.toDate);
                }
                if (settings.activeRange) {
                    setActiveRange(settings.activeRange);
                } else {
                    setActiveRange('custom');
                }
            }

            if (typeof settings.controlsExpanded === 'boolean') {
                setControlsExpanded(settings.controlsExpanded);
            }

            if (settings.groupingOption) {
                setGroupingOption(settings.groupingOption);
            }
        } else {
            applyDateRange('today');
        }

        setSettingsLoaded(true);
    }, []);

    // Toggle column visibility
    const toggleColumn = (columnName) => {
        setVisibleColumns(prev => ({
            ...prev,
            [columnName]: !prev[columnName]
        }));
    };

    // Toggle group expansion
    const toggleGroup = (groupKey) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupKey]: !prev[groupKey]
        }));
    };

    // Fetch timecard data
    const fetchReportData = useCallback(async () => {
        if (!propertiesId) {
            toast.error('Property ID not found. Please log in again.');
            return;
        }

        setLoading(true);
        try {
            const response = await nbrApi.post('/gettimeclockdata', {
                properties_id: propertiesId,
                time_start: fromDate,
                time_stop: toDate
            });

            if (response?.data && Array.isArray(response.data)) {
                setTimecardData(response.data);
                if (response.data.length === 0) {
                    toast.info('No timecard data found for the selected date range');
                } else {
                    toast.success(`Loaded ${response.data.length} timecard record(s)`);
                }
            } else {
                setTimecardData([]);
                toast.info('No timecard data found for the selected date range');
            }
        } catch (error) {
            console.error('Error fetching timecard data:', error);
            toast.error('Failed to fetch timecard data. Please try again.');
            setTimecardData([]);
        } finally {
            setLoading(false);
        }
    }, [propertiesId, fromDate, toDate]);

    // Initialize with today's range
    useEffect(() => {
        if (settingsLoaded && propertiesId) {
            fetchReportData();
        }
    }, [propertiesId, settingsLoaded]);

    // Auto-fetch data when dates change (after initial load)
    useEffect(() => {
        if (settingsLoaded && propertiesId) {
            fetchReportData();
        }
    }, [fromDate, toDate, propertiesId, settingsLoaded, fetchReportData]);

    const formatCellValue = (value, columnKey) => {
        if (value === null || value === undefined) return '-';
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        if (typeof value === 'object') return JSON.stringify(value);
        
        // Format time duration fields (seconds, minutes, hours) - show decimal with formatted time in parentheses
        if (columnKey === 'timeclock_segment_seconds') {
            const totalSeconds = parseFloat(value) || 0;
            const decimalValue = totalSeconds.toFixed(2);
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = Math.floor(totalSeconds % 60);
            
            let formattedTime = '';
            if (hours > 0) {
                formattedTime = `${hours}h ${minutes}m ${seconds}s`;
            } else if (minutes > 0) {
                formattedTime = `${minutes}m ${seconds}s`;
            } else {
                formattedTime = `${seconds}s`;
            }
            
            return `${decimalValue} (${formattedTime})`;
        }
        
        if (columnKey === 'timeclock_segment_minutes') {
            const totalMinutes = parseFloat(value) || 0;
            const decimalValue = totalMinutes.toFixed(2);
            const hours = Math.floor(totalMinutes / 60);
            const mins = Math.floor(totalMinutes % 60);
            const secs = Math.round((totalMinutes % 1) * 60);
            
            let formattedTime = '';
            if (hours > 0) {
                formattedTime = `${hours}h ${mins}m ${secs}s`;
            } else if (mins > 0 || secs > 0) {
                formattedTime = `${mins}m ${secs}s`;
            } else {
                formattedTime = '0s';
            }
            
            return `${decimalValue} (${formattedTime})`;
        }
        
        if (columnKey === 'timeclock_segment_hours') {
            const totalHours = parseFloat(value) || 0;
            const decimalValue = totalHours.toFixed(2);
            const hours = Math.floor(totalHours);
            const minutes = Math.round((totalHours % 1) * 60);
            const seconds = Math.round(((totalHours % 1) * 60 % 1) * 60);
            
            let formattedTime = '';
            if (hours > 0 && minutes > 0 && seconds > 0) {
                formattedTime = `${hours}h ${minutes}m ${seconds}s`;
            } else if (hours > 0 && minutes > 0) {
                formattedTime = `${hours}h ${minutes}m`;
            } else if (hours > 0) {
                formattedTime = `${hours}h`;
            } else if (minutes > 0 && seconds > 0) {
                formattedTime = `${minutes}m ${seconds}s`;
            } else if (minutes > 0) {
                formattedTime = `${minutes}m`;
            } else if (seconds > 0) {
                formattedTime = `${seconds}s`;
            } else {
                formattedTime = '0h';
            }
            
            return `${decimalValue} (${formattedTime})`;
        }
        
        // Format time-only fields (shift_start, shift_end) - these are HH:mm:ss format
        if (columnKey === 'shift_start' || columnKey === 'shift_end') {
            if (typeof value === 'string' && value.match(/^\d{2}:\d{2}:\d{2}$/)) {
                return value;
            }
            return value;
        }
        
        // Format date/time fields - standardize to consistent format
        if (columnKey && (columnKey.toLowerCase().includes('date') || 
            columnKey.toLowerCase().includes('time') || 
            columnKey.toLowerCase().includes('starting') || 
            columnKey.toLowerCase().includes('ending') ||
            columnKey.toLowerCase().includes('actual'))) {
            const date = moment(value);
            if (date.isValid()) {
                // Standard format: Always use YYYY-MM-DD HH:mm:ss for datetime fields
                // Check if the value has time information
                const hasTime = typeof value === 'string' && (
                    value.includes('T') || 
                    value.includes(' ') ||
                    value.match(/\d{4}-\d{2}-\d{2}[\sT]\d{2}:\d{2}/)
                ) || (date.hour() !== 0 || date.minute() !== 0 || date.second() !== 0);
                
                // Use standard datetime format if time is present, otherwise date only
                if (hasTime) {
                    return date.format('YYYY-MM-DD HH:mm:ss');
                } else {
                    return date.format('YYYY-MM-DD');
                }
            }
        }
        
        // Format numeric values with appropriate precision
        if (typeof value === 'number') {
            // If it's a decimal with many places, round to 2 decimal places
            if (value % 1 !== 0 && value.toString().split('.')[1]?.length > 2) {
                return value.toFixed(2);
            }
            return value;
        }
        
        return value;
    };

    const formatColumnHeader = (key) => {
        // Convert snake_case or camelCase to Title Case
        return key
            .replace(/_/g, ' ')
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();
    };

    // Check if column should be right-aligned
    const isRightAlignedColumn = (columnKey) => {
        const rightAlignedColumns = [
            'timeclock_segment_seconds',
            'timeclock_segment_minutes',
            'timeclock_segment_hours',
            'resources_id'
        ];
        return rightAlignedColumns.includes(columnKey);
    };

    // Get grouping key and display name from a record
    const getGroupingInfo = (record) => {
        switch (groupingOption) {
            case 'resource':
                // Try to get resource name first, then fall back to ID
                // Check various field name patterns
                const resourceName = record.resource_name || 
                                   record.resources_name ||
                                   record.resource ||
                                   (record.resource_first_name && record.resource_last_name ? 
                                    `${record.resource_first_name} ${record.resource_last_name}` : null) ||
                                   record.resource_first_name ||
                                   record.resources_first_name;
                const resourceId = record.resources_id || record.resource_id;
                // Use ID as key for grouping, name for display
                const displayName = resourceName || resourceId || 'Unknown Resource';
                const groupKey = resourceId || resourceName || 'Unknown Resource';
                return {
                    key: groupKey,
                    display: displayName
                };
            case 'date':
                // Extract date from datetime fields - check all fields that might contain date information
                const dateFields = [
                    'date_local',
                    'date',
                    'work_date',
                    'shift_date',
                    'timeclock_segment_starting',
                    'timeclock_segment_ending',
                    'time_in_local',
                    'time_out_local',
                    'actual_starting',
                    'actual_ending'
                ];
                
                // First, try explicit date fields
                for (const field of dateFields) {
                    if (record[field]) {
                        const date = moment(record[field]);
                        if (date.isValid()) {
                            const dateStr = date.format('YYYY-MM-DD');
                            return { key: dateStr, display: dateStr };
                        }
                    }
                }
                
                // If no explicit date field found, try any field with 'date' in the name
                for (const key in record) {
                    if (key.toLowerCase().includes('date') && record[key]) {
                        const date = moment(record[key]);
                        if (date.isValid()) {
                            const dateStr = date.format('YYYY-MM-DD');
                            return { key: dateStr, display: dateStr };
                        }
                    }
                }
                
                // If still no date found, try any datetime-like field
                for (const key in record) {
                    if (record[key] && typeof record[key] === 'string') {
                        const date = moment(record[key]);
                        if (date.isValid() && (key.toLowerCase().includes('time') || key.toLowerCase().includes('local'))) {
                            const dateStr = date.format('YYYY-MM-DD');
                            return { key: dateStr, display: dateStr };
                        }
                    }
                }
                
                return { key: 'Unknown Date', display: 'Unknown Date' };
            case 'role':
                // Try common role field names
                const roleName = record.role_name || record.role || record.resources_role || record.job_title || 'Unknown Role';
                return { key: roleName, display: roleName };
            default:
                return null;
        }
    };

    // Group data based on selected grouping option
    const groupedData = useMemo(() => {
        if (groupingOption === 'none' || timecardData.length === 0) {
            return null;
        }

        const groups = {};
        timecardData.forEach(record => {
            const groupInfo = getGroupingInfo(record);
            if (!groupInfo) return;
            
            const key = groupInfo.key;
            if (!groups[key]) {
                groups[key] = {
                    key,
                    display: groupInfo.display,
                    records: []
                };
            }
            groups[key].records.push(record);
        });

        // Convert to array and sort
        return Object.values(groups).sort((a, b) => {
            // Sort dates chronologically, others alphabetically
            if (groupingOption === 'date') {
                return a.key.localeCompare(b.key);
            }
            return a.display.localeCompare(b.display);
        });
    }, [timecardData, groupingOption]);

    // Calculate group totals for time columns
    const calculateGroupTotals = (records) => {
        const totals = {
            timeclock_segment_seconds: 0,
            timeclock_segment_minutes: 0,
            timeclock_segment_hours: 0
        };

        records.forEach(record => {
            if (record.timeclock_segment_seconds) {
                totals.timeclock_segment_seconds += parseFloat(record.timeclock_segment_seconds) || 0;
            }
            if (record.timeclock_segment_minutes) {
                totals.timeclock_segment_minutes += parseFloat(record.timeclock_segment_minutes) || 0;
            }
            if (record.timeclock_segment_hours) {
                totals.timeclock_segment_hours += parseFloat(record.timeclock_segment_hours) || 0;
            }
        });

        return totals;
    };

    return (
        <div className="container-fluid">
            {/* Header */}
            <div className="row">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body">
                            <h3 className="mb-0 text-primary fw-bold">
                                <i className="bi bi-clock-history me-2"></i>
                                Timecard Report
                            </h3>
                            <p className="text-muted mb-0 mt-2">
                                View timecard data for housekeepers to track time worked
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Controls */}
            <div className="row mb-2">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body">
                            <div className="row align-items-end g-3">
                                {/* From Date */}
                                <div className="col-12 col-md-6 col-lg-2">
                                    <label htmlFor="fromDate" className="form-label fw-bold text-primary">
                                        <i className="bi bi-calendar-event me-2"></i>
                                        From Date
                                    </label>
                                    <input
                                        type="date"
                                        id="fromDate"
                                        className="form-control"
                                        value={fromDate}
                                        onChange={(e) => {
                                            setFromDate(e.target.value);
                                            if (activeRange !== 'custom') {
                                                setActiveRange('custom');
                                            }
                                        }}
                                    />
                                </div>

                                {/* To Date */}
                                <div className="col-12 col-md-6 col-lg-2">
                                    <label htmlFor="toDate" className="form-label fw-bold text-primary">
                                        <i className="bi bi-calendar-check me-2"></i>
                                        To Date
                                    </label>
                                    <input
                                        type="date"
                                        id="toDate"
                                        className="form-control"
                                        value={toDate}
                                        onChange={(e) => {
                                            setToDate(e.target.value);
                                            if (activeRange !== 'custom') {
                                                setActiveRange('custom');
                                            }
                                        }}
                                    />
                                </div>

                                {/* Quick Ranges */}
                                <div className="col-12 col-md-6 col-lg-4">
                                    <label className="form-label fw-bold text-primary d-block">
                                        <i className="bi bi-lightning-charge me-2"></i>
                                        Quick Ranges
                                    </label>
                                    <div className="d-flex flex-wrap gap-2">
                                        {RANGE_OPTIONS.map(option => (
                                            <button
                                                key={option.value}
                                                type="button"
                                                className={`btn btn-sm ${activeRange === option.value ? 'btn-primary' : 'btn-outline-primary'}`}
                                                onClick={() => applyDateRange(option.value)}
                                                style={{ flex: '1 1 auto', minWidth: '100px' }}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Generate Button */}
                                <div className="col-12 col-md-6 col-lg-2">
                                    <label className="form-label fw-bold text-primary d-block d-lg-none">
                                        &nbsp;
                                    </label>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-primary w-100"
                                        onClick={fetchReportData}
                                        disabled={loading}
                                    >
                                        <i className="bi bi-search me-2"></i>
                                        {loading ? 'Loading...' : 'Generate'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Grouping Options */}
            {timecardData.length > 0 && (
                <div className="row mb-2">
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-body">
                                <label className="form-label fw-bold text-primary mb-2">
                                    <i className="bi bi-diagram-3 me-2"></i>
                                    Group By
                                </label>
                                <div className="d-flex flex-wrap gap-2">
                                    {GROUPING_OPTIONS.map(option => (
                                        <button
                                            key={option.value}
                                            type="button"
                                            className={`btn btn-sm ${groupingOption === option.value ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => setGroupingOption(option.value)}
                                            style={{ flex: '1 1 auto', minWidth: '120px' }}
                                        >
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Column Selection Controls */}
            {timecardData.length > 0 && (
                <div className="row mb-2">
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div
                                className="card-header bg-white"
                                style={{ cursor: 'pointer' }}
                                onClick={() => setControlsExpanded(!controlsExpanded)}
                            >
                                <div className="d-flex justify-content-between align-items-center py-2 w-100">
                                    <h6 className="mb-0 fw-bold">
                                        <i className={`bi bi-chevron-${controlsExpanded ? 'down' : 'right'} me-2`}></i>
                                        Column Visibility
                                    </h6>
                                </div>
                            </div>
                            {controlsExpanded && (
                                <div className="card-body">
                                    <label className="fw-bold text-dark mb-3 d-block">
                                        Select columns to display
                                    </label>
                                    <div className="row g-3">
                                        {allColumns.map(column => (
                                            <div key={column} className="col-md-3 col-sm-4 col-6">
                                                <div
                                                    className="form-check p-2 rounded"
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id={`column_${column}`}
                                                        checked={visibleColumns[column] || false}
                                                        onChange={() => toggleColumn(column)}
                                                        style={{
                                                            cursor: 'pointer',
                                                            marginTop: 7
                                                        }}
                                                    />
                                                    <label
                                                        className="form-check-label ms-2"
                                                        htmlFor={`column_${column}`}
                                                        style={{
                                                            cursor: 'pointer',
                                                            fontSize: '0.85rem'
                                                        }}
                                                    >
                                                        {formatColumnHeader(column)}
                                                    </label>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Table */}
            <div className="row">
                <div className="col-12">
                    <div className="card shadow-sm border-0" style={{ position: 'relative' }}>
                        <div className="card-body" style={{ position: 'relative', minHeight: '300px' }}>
                            {loading && (
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    zIndex: 10,
                                    borderRadius: '0.375rem'
                                }}>
                                    <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-3 text-muted">Loading, please wait...</p>
                                </div>
                            )}
                            {timecardData.length > 0 ? (
                                <div className="table-responsive">
                                    <Table responsive bordered hover className="table shadow-hover mb-4 dataTable no-footer" style={{ backgroundColor: '#fff' }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#f8f9fa' }}>
                                                {visibleColumnOrder.map((column, index) => (
                                                    <th 
                                                        key={index} 
                                                        className="fs-14 font-w600 border-0 text-primary" 
                                                        style={{ 
                                                            backgroundColor: '#f8f9fa',
                                                            textAlign: isRightAlignedColumn(column) ? 'right' : 'left'
                                                        }}
                                                    >
                                                        {formatColumnHeader(column)}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {groupingOption === 'none' ? (
                                                // Render without grouping
                                                timecardData.map((record, rowIndex) => (
                                                    <tr key={rowIndex} style={{ backgroundColor: '#fff' }}>
                                                        {visibleColumnOrder.map((column, colIndex) => (
                                                            <td 
                                                                key={colIndex} 
                                                                className="fs-14" 
                                                                style={{ 
                                                                    color: '#000', 
                                                                    backgroundColor: '#fff',
                                                                    textAlign: isRightAlignedColumn(column) ? 'right' : 'left'
                                                                }}
                                                            >
                                                                {formatCellValue(record[column], column)}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))
                                            ) : (
                                                // Render with grouping
                                                groupedData && groupedData.map((group, groupIndex) => {
                                                    const groupKey = group.key;
                                                    const isExpanded = expandedGroups[groupKey] !== false; // Default to expanded
                                                    const groupTotals = calculateGroupTotals(group.records);
                                                    
                                                    return (
                                                        <React.Fragment key={groupIndex}>
                                                            {/* Group Header */}
                                                            <tr 
                                                                style={{ 
                                                                    backgroundColor: '#e3f2fd', 
                                                                    fontWeight: 'bold',
                                                                    cursor: 'pointer'
                                                                }}
                                                                onClick={() => toggleGroup(groupKey)}
                                                            >
                                                                {(() => {
                                                                    const timeColumns = ['timeclock_segment_seconds', 'timeclock_segment_minutes', 'timeclock_segment_hours'];
                                                                    const firstNonTimeColumnIndex = visibleColumnOrder.findIndex(col => !timeColumns.includes(col));
                                                                    
                                                                    return visibleColumnOrder.map((column, colIndex) => {
                                                                        const isTimeColumn = timeColumns.includes(column);
                                                                        const isGroupNameColumn = colIndex === firstNonTimeColumnIndex;
                                                                        
                                                                        if (isTimeColumn) {
                                                                            return (
                                                                                <td 
                                                                                    key={colIndex}
                                                                                    className="fs-14 fw-bold text-primary"
                                                                                    style={{ 
                                                                                        padding: '12px 16px',
                                                                                        borderBottom: '2px solid #2196f3',
                                                                                        textAlign: 'right'
                                                                                    }}
                                                                                >
                                                                                    {formatCellValue(groupTotals[column], column)}
                                                                                </td>
                                                                            );
                                                                        } else if (isGroupNameColumn) {
                                                                            return (
                                                                                <td 
                                                                                    key={colIndex}
                                                                                    className="fs-14 fw-bold text-primary"
                                                                                    style={{ 
                                                                                        padding: '12px 16px',
                                                                                        borderBottom: '2px solid #2196f3'
                                                                                    }}
                                                                                >
                                                                                    <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'} me-2`}></i>
                                                                                    {group.display}
                                                                                    <span className="text-muted ms-2" style={{ fontWeight: 'normal', fontSize: '0.9em' }}>
                                                                                        ({group.records.length} record{group.records.length !== 1 ? 's' : ''})
                                                                                    </span>
                                                                                </td>
                                                                            );
                                                                        } else {
                                                                            return (
                                                                                <td 
                                                                                    key={colIndex}
                                                                                    className="fs-14 fw-bold"
                                                                                    style={{ 
                                                                                        padding: '12px 16px',
                                                                                        borderBottom: '2px solid #2196f3'
                                                                                    }}
                                                                                >
                                                                                </td>
                                                                            );
                                                                        }
                                                                    });
                                                                })()}
                                                            </tr>
                                                            {/* Group Records */}
                                                            {isExpanded && group.records.map((record, rowIndex) => (
                                                                <tr key={`${groupIndex}-${rowIndex}`} style={{ backgroundColor: '#fff' }}>
                                                                    {visibleColumnOrder.map((column, colIndex) => (
                                                                        <td 
                                                                            key={colIndex} 
                                                                            className="fs-14" 
                                                                            style={{ 
                                                                                color: '#000', 
                                                                                backgroundColor: '#fff',
                                                                                textAlign: isRightAlignedColumn(column) ? 'right' : 'left'
                                                                            }}
                                                                        >
                                                                            {formatCellValue(record[column], column)}
                                                                        </td>
                                                                    ))}
                                                                </tr>
                                                            ))}
                                                        </React.Fragment>
                                                    );
                                                })
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            ) : (
                                !loading && (
                                    <div className="text-center py-5">
                                        <i className="bi bi-clock-history" style={{ fontSize: '48px', color: '#ccc' }}></i>
                                        <p className="text-muted mt-3">
                                            No timecard data available for the selected date range
                                        </p>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default TimecardReport;

